import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, parseISO } from 'date-fns';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import {
    MdOutlineEdit,
    MdMerge,
    MdBlock,
    MdOutlinePowerSettingsNew,
    MdCheck,
} from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { fetchMedicalCenters, fetchUSStates } from '~/services/medical-centers';
import AddMedicalCenterDrawer from '~/components/MedicalCenters/AddMedicalCenterDrawer';
import EditMedicalCenterDrawer from '~/pages/Outreach/EditMedicalCenterDrawer';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import { Pagination } from '~/components/Pagination';
import AsyncSelect from 'react-select/async';
import { FaSearch } from 'react-icons/fa';
import { colorStyles } from '~/common/SelectStyles';

interface MedicalCenter {
    id: number;
    name: string;
    location: string;
    created_date: string;
    is_active: boolean;
    approved_by_person_id: boolean;
    total_verified_patients: number;
    total_unverified_patients: number;
    referrals: number;
}

interface SelectOption {
    value: string;
    label: string;
}

const columnHelper = createColumnHelper<MedicalCenter>();

const columns = [
    columnHelper.accessor('name', {
        id: 'name',
        header: () => 'Name',
        cell: (info) => (
            <Link
                className='font-bold text-primary'
                to={`/medical-centers/${info.row.original.id}`}
            >
                {info.renderValue()}
            </Link>
        ),
    }),
    columnHelper.accessor('location', {
        header: () => 'State',
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor(
        (props) =>
            props.created_date
                ? formatDate(parseISO(props.created_date), 'MM/dd/yy')
                : '',
        {
            id: 'created_date',
            header: () => 'Date',
            cell: (props) => props.renderValue(),
        }
    ),
    columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (info) => (
            <div
                className={`inline-flex rounded-full px-3 py-1 text-sm hover:bg-opacity-90 ${
                    info.getValue() === true
                        ? 'bg-primary'
                        : 'bg-whiten text-secondary'
                }`}
            >
                {info.getValue() === true ? 'Active' : 'Inactive'}
            </div>
        ),
    }),
    columnHelper.accessor('approved_by_person_id', {
        header: 'Verified',
        cell: (info) =>
            info.getValue() ? (
                <MdCheck className='text-primary' size={20} />
            ) : (
                ''
            ),
    }),
    columnHelper.accessor('total_verified_patients', {
        header: () => <span>Verified Patients</span>,
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('total_unverified_patients', {
        header: 'Unverified Patients',
        cell: (info) => {
            return info.renderValue();
        },
    }),
    columnHelper.accessor('referrals', {
        header: 'Referrals',
        cell: () => {
            return 'NEED DATA';
        },
    }),
];

const MedicalCenters: React.FC = () => {
    const [data, setData] = useState<MedicalCenter[]>([]);
    const [isCreateMedCenterShowing, setIsCreatedMedCenterShowing] =
        useState(false);
    const [isCreateFormShowing, setIsCreateFormShowing] = useState(false);
    const [createFormInitialName, setCreateFormInitialName] = useState('');
    const [pages, setPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
    const [filters, setFilters] = useState({
        page: 1,
        status: '',
        verificationStatus: '',
        name: '',
        state: '',
    });
    const [debouncedName, setDebouncedName] = useState('');

    const { page } = filters;

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    // Debounce the name filter
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(filters.name);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filters.name]);

    // Fetch medical centers when filters change
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const results = await fetchMedicalCenters({
                    ...filters,
                    name: debouncedName,
                });
                setData(results.results);
                setTotalCount(results.count);
                setPages(Math.ceil(results.count / 20));
            } catch (error) {
                console.error('Error fetching medical centers:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.page, filters.status, filters.verificationStatus, filters.state, debouncedName]);

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string | number
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            ...(key !== 'page' ? { page: 1 } : {}),
        }));
    };

    const loadUsStatesOptions = async (): Promise<SelectOption[]> => {
        const states = await fetchUSStates();
        const mappedStates = states?.map((state: { state: string }) => ({
            value: state?.state,
            label: state?.state,
        }));

        return mappedStates || [];
    };

    return (
        <>
            <Breadcrumb pageName='Medical Centers' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex items-center gap-4'>
                        <div className='relative w-full min-w-[125px] max-w-[175px]'>
                            <input
                                type='text'
                                value={filters.name}
                                onChange={(e) =>
                                    handleFilterChange('name', e.target.value)
                                }
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                placeholder='Name'
                            />
                            <FaSearch
                                color='gray'
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                            />
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <AsyncSelect
                                placeholder='Select State'
                                cacheOptions
                                loadOptions={loadUsStatesOptions}
                                className='w-full'
                                defaultOptions
                                isSearchable={false}
                                value={selectedState}
                                onChange={(option) => {
                                    setSelectedState(option);
                                    handleFilterChange(
                                        'state',
                                        option?.value || ''
                                    );
                                }}
                                isClearable
                                styles={colorStyles}
                            />
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <select
                                name='status'
                                id='status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                value={filters.status}
                                onChange={(e) =>
                                    handleFilterChange('status', e.target.value)
                                }
                            >
                                <option value=''>Status</option>
                                <option value='active'>Active</option>
                                <option value='inactive'>Inactive</option>
                            </select>
                        </div>
                        <div className='w-full min-w-[180px] '>
                            <select
                                name='approval-status'
                                id='approval-status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                value={filters.verificationStatus}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'verificationStatus',
                                        e.target.value
                                    )
                                }
                            >
                                <option value=''>Verification Status</option>
                                <option value='verified'>Verified</option>
                                <option value='unverified'>Unverified</option>
                            </select>
                        </div>
                    </div>

                    <Link
                        onClick={() => setIsCreatedMedCenterShowing(true)}
                        to='#'
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                    >
                        <FaPlus />
                        Add Center
                    </Link>
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(20, (totalCount || 0) - (page - 1) * 20)} of {totalCount || 0} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdBlock className='text-red' size={18} />
                            Deactivate
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlinePowerSettingsNew
                                className='text-primary'
                                size={18}
                            />
                            Reactivate
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                </span>
                                                <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                    <span className='inline-block'>
                                                        <svg
                                                            xmlns='http://www.w3.org/2000/svg'
                                                            width='17'
                                                            height='16'
                                                            viewBox='0 0 17 16'
                                                            fill='none'
                                                        >
                                                            <path
                                                                d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                                                fill='#131212'
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          >
                                              <CampaignActionDropdown />
                                          </td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    pages={pages}
                    setPage={(val: number) => handleFilterChange('page', val)}
                    totalCount={totalCount}
                />
            </section>
            <AddMedicalCenterDrawer
                isOpen={isCreateMedCenterShowing}
                setIsOpen={setIsCreatedMedCenterShowing}
                onAddNew={(typedName) => {
                    setCreateFormInitialName(typedName);
                    setIsCreateFormShowing(true);
                }}
            />
            <EditMedicalCenterDrawer
                isOpen={isCreateFormShowing}
                setIsOpen={setIsCreateFormShowing}
                initialName={createFormInitialName}
            />
        </>
    );
};

export default MedicalCenters;
